import { SiteReportEventName, logEventV2 } from "react-migration/lib/util/logEventV2";
import { useCallback, useState } from "react";

import ROUTES from "src/js/router/routes";
import { area } from "@turf/turf";
import { router } from "src/js/router/react-router";
import { useSiteById } from "react-migration/domains/sites/hooks/useSiteById";
import { useSiteReportConfiguration } from "react-migration/layouts/SiteReport/useSiteReportConfiguration";
import { useSiteReportLogo } from "./useSiteReportLogo";
import { AssessmentLayer } from "./types";

export function useToggleList(initialValue: string[] = []) {
  const [list, setList] = useState(initialValue);

  const toggle = useCallback((value: string) => {
    setList((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  }, []);

  return [list, toggle] as const;
}

export function usePrintReport(siteId: string | undefined, activeAssessmentLayers: AssessmentLayer[]) {
  return useCallback(() => {
    if (!siteId) return;

    const layerIds = [...new Set(activeAssessmentLayers.map((item) => item.layerId))];

    const siteReportPrintViewUrlParams = {
      isPrint: "true",
      siteId: String(siteId),
      activeAssessmentLayers: layerIds.join(","),
    };
    router.push({
      name: ROUTES.SITE_REPORT,
      query: siteReportPrintViewUrlParams,
    });
  }, [siteId, activeAssessmentLayers]);
}

export function usePrintLogging(siteId: string, assessmentState: AssessmentLayer[], isShared = false) {
  const { site } = useSiteById(siteId, { isShared });
  const { logo } = useSiteReportLogo(siteId!);
  const [siteReportConfiguration] = useSiteReportConfiguration(siteId!);

  const printDownloadClicked = useCallback(() => {
    logEventV2({
      name: SiteReportEventName.SITE_REPORT_SAVE_AS_PDF_CLICKED,
      properties: {
        siteId,
      },
    });
  }, [siteId]);

  const printDownloaded = useCallback(() => {
    const sectionsToggled = [...new Set(assessmentState.map((item) => item.layerId))];

    logEventV2({
      name: SiteReportEventName.SITE_REPORT_DOWNLOADED,
      properties: {
        addedLogo: !!logo,
        compsRemoved: !!siteReportConfiguration?.excludeComparableIds?.length,
        planningRemoved: !!siteReportConfiguration?.excludePlanningApplicationIds?.length,
        sectionsToggled,
        siteAreaInMeters: site?.geometry ? +area(site?.geometry).toFixed(2) : 0,
        siteId,
      },
    });
  }, [
    assessmentState,
    logo,
    site?.geometry,
    siteId,
    siteReportConfiguration?.excludeComparableIds?.length,
    siteReportConfiguration?.excludePlanningApplicationIds?.length,
  ]);

  return { printDownloadClicked, printDownloaded };
}
